import React from "react";
import type { LiHTMLAttributes, ReactNode } from "react";
import type { MarkdownPreviewProps } from "@uiw/react-markdown-preview";
import { CheckboxRenderer, type ICheckboxChild, type ICheckboxData } from "../CheckboxRenderer/CheckboxRenderer";

const CHECKBOX_TYPE = "input";
const CHECKBOX_INPUT_TYPE = "checkbox";

function isCheckboxElement(child: unknown): child is ICheckboxChild {
  if (child !== null && typeof child === "object") {
    const childObj = child as Record<string, unknown>;
    return (
      childObj.type === CHECKBOX_TYPE &&
      (childObj.props as Record<string, unknown>)?.type === CHECKBOX_INPUT_TYPE
    );
  }
  return false;
}

function hasCheckboxChild(children: unknown[]): boolean {
  return children.some((child) => isCheckboxElement(child));
}

function isNestedList(child: unknown): boolean {
  if (React.isValidElement(child)) {
    return child.type === "ul" || child.type === "ol";
  }
  return false;
}

function extractCheckboxData(children: React.ReactNode[]): ICheckboxData {
  const checkboxItem = children.find((child) => isCheckboxElement(child)) as
    | ICheckboxChild
    | undefined;
  const isChecked = (checkboxItem?.props?.checked as boolean) ?? false;
  const textContent = children.filter((child) => !isCheckboxElement(child) && !isNestedList(child));
  const nestedContent = children.filter((child) => isNestedList(child));

  return {
    isChecked,
    textContent,
    nestedContent,
  };
}

export function createListItemRenderer(): Pick<NonNullable<MarkdownPreviewProps["components"]>, "li"> {
  return {
    li: (props: LiHTMLAttributes<HTMLLIElement> & { children?: React.ReactNode }): ReactNode => {
      const { children } = props;
      const childArray = Array.isArray(children) ? children : [children];

      if (hasCheckboxChild(childArray as unknown[])) {
        const { isChecked, textContent, nestedContent } = extractCheckboxData(childArray as React.ReactNode[]);
        return <CheckboxRenderer isChecked={isChecked} textContent={textContent} nestedContent={nestedContent} />;
      }

      return <li {...props} />;
    },
  };
}

